import { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ButtonBase from '@mui/material/ButtonBase';

// third party
import { FormattedMessage } from 'react-intl';
import { useSnackbar } from 'notistack';

// project imports
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';
import { useNavTheme } from 'contexts/NavThemeContext';
import { PASSWORD_REGEX, PASSWORD_COMPLEXITY_MSG } from 'utils/password-strength';

// assets
import { IconLogout, IconSettings, IconUser, IconMail, IconLock, IconKey } from '@tabler/icons-react';
import useConfig from 'hooks/useConfig';

// ==============================|| USER AVATAR WITH INITIALS ||============================== //

interface UserAvatarProps {
  name?: string;
  size?: number;
}

function UserAvatar({ name, size = 34 }: UserAvatarProps) {
  // Get initials from name
  const getInitials = (name?: string) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Generate a consistent color based on name
  const getColor = (name?: string) => {
    if (!name) return { bg: '#6366f1', text: '#ffffff' };
    const colors = [
      { bg: '#6366f1', text: '#ffffff' }, // Indigo
      { bg: '#8b5cf6', text: '#ffffff' }, // Purple
      { bg: '#06b6d4', text: '#ffffff' }, // Cyan
      { bg: '#10b981', text: '#ffffff' }, // Emerald
      { bg: '#f59e0b', text: '#ffffff' }, // Amber
      { bg: '#ec4899', text: '#ffffff' }, // Pink
      { bg: '#3b82f6', text: '#ffffff' }, // Blue
    ];
    const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[index];
  };

  const initials = getInitials(name);
  const color = getColor(name);

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: color.bg,
        color: color.text,
        fontSize: size * 0.38,
        fontWeight: 600,
        fontFamily: '"JetBrains Mono", monospace',
        letterSpacing: '-0.02em',
        flexShrink: 0,
      }}
    >
      {initials}
    </Box>
  );
}

// ==============================|| PROFILE MENU ||============================== //

export default function ProfileSection() {
  const theme = useTheme();
  const {
    state: { borderRadius }
  } = useConfig();
  const { mode, tokens } = useNavTheme();
  const isLight = mode === 'light';

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { logout, user, changePassword, updateProfile } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [openSettingsModal, setOpenSettingsModal] = useState(false);

  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  /**
   * anchorRef is used on different components and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef<any>(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const handleOpenSettings = () => {
    setOpenSettingsModal(true);
    setOpen(false);
  };

  const handleCloseSettings = () => {
    setOpenSettingsModal(false);
    // Reset form state when closing
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setError('');
  };

  const handleSaveSettings = async () => {
    // Validate password fields
    if (!passwordForm.newPassword && !passwordForm.confirmPassword && !passwordForm.currentPassword) {
      // No password change requested, just close
      handleCloseSettings();
      return;
    }

    // For OAuth users without password, currentPassword can be empty
    if (user?.hasPassword && !passwordForm.currentPassword) {
      setError('Current password is required');
      return;
    }

    if (!passwordForm.newPassword) {
      setError('New password is required');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    if (!PASSWORD_REGEX.test(passwordForm.newPassword)) {
      setError(PASSWORD_COMPLEXITY_MSG);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await changePassword?.(passwordForm.currentPassword || '', passwordForm.newPassword);

      const hadNoPassword = !user?.hasPassword;
      enqueueSnackbar(
        hadNoPassword
          ? 'Password set successfully! You can now also log in with email and password.'
          : 'Password changed successfully',
        { variant: 'success' }
      );

      // Refresh user profile to update hasPassword state
      await updateProfile?.();

      handleCloseSettings();
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        aria-label="user-account"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height: '42px',
          pl: 0.5,
          pr: 1.5,
          borderRadius: '12px',
          background: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.04)',
          border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            background: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)',
            border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'}`,
          },
        }}
      >
        <UserAvatar name={user?.name} size={32} />
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.06)',
            color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <IconSettings stroke={1.5} size="16px" />
        </Box>
      </ButtonBase>
      <Popper
        placement="bottom"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 14]
            }
          }
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                sx={{
                  background: isLight ? '#ffffff' : 'rgba(20, 22, 30, 0.98)',
                  border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
                  borderRadius: '12px',
                  boxShadow: isLight
                    ? '0 4px 24px rgba(0, 0, 0, 0.08)'
                    : '0 4px 24px rgba(0, 0, 0, 0.4)',
                  overflow: 'hidden',
                }}
              >
                {open && (
                  <Box sx={{ minWidth: 220 }}>
                    {/* User info header */}
                    <Box sx={{ p: 2, pb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <UserAvatar name={user?.name} size={40} />
                        <Box>
                          <Typography
                            sx={{
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              color: isLight ? '#1e293b' : 'rgba(255, 255, 255, 0.95)',
                            }}
                          >
                            {user?.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)',
                            }}
                          >
                            {user?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        mx: 2,
                        height: '1px',
                        background: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
                      }}
                    />

                    {/* Menu items */}
                    <Box sx={{ p: 1 }}>
                      <List
                        component="nav"
                        sx={{
                          p: 0,
                          '& .MuiListItemButton-root': {
                            borderRadius: '8px',
                            py: 1,
                            px: 1.5,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              background: isLight ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                            },
                          },
                          '& .MuiListItemIcon-root': {
                            minWidth: 32,
                            color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.6)',
                          }
                        }}
                      >
                        <ListItemButton onClick={handleOpenSettings}>
                          <ListItemIcon>
                            <IconSettings stroke={1.5} size="18px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: '0.85rem',
                                  color: isLight ? '#374151' : 'rgba(255, 255, 255, 0.85)',
                                }}
                              >
                                <FormattedMessage id="account-settings" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                        <ListItemButton onClick={handleLogout}>
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="18px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: '0.85rem',
                                  color: isLight ? '#374151' : 'rgba(255, 255, 255, 0.85)',
                                }}
                              >
                                <FormattedMessage id="logout" />
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>

      {/* User Settings Modal */}
      <Dialog
        open={openSettingsModal}
        onClose={handleCloseSettings}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              background: isLight ? '#ffffff' : 'rgba(20, 22, 30, 0.98)',
              border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
              borderRadius: '16px',
              boxShadow: isLight
                ? '0 25px 80px rgba(15, 23, 42, 0.12)'
                : '0 25px 80px rgba(0, 0, 0, 0.5)',
              overflow: 'hidden',
            }
          },
          backdrop: {
            sx: {
              backgroundColor: isLight ? 'rgba(15, 23, 42, 0.4)' : 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
            }
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 3, px: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.04)',
                border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}`,
                color: tokens.accentPrimary,
              }}
            >
              <IconSettings size={24} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  fontFamily: '"Satoshi", "Inter", sans-serif',
                  color: tokens.textPrimary,
                  letterSpacing: '-0.02em',
                }}
              >
                Account Settings
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  color: tokens.textMuted,
                  mt: 0.25,
                }}
              >
                Update your personal information and password
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 2 }}>
          <Stack spacing={3}>
            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ borderRadius: '10px' }}>
                {error}
              </Alert>
            )}

            {/* Personal Information Section - Read Only */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconUser size={18} style={{ color: isLight ? '#1e293b' : '#fafafa' }} />
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    fontFamily: '"Satoshi", sans-serif',
                    color: isLight ? '#1e293b' : '#fafafa',
                  }}
                >
                  Personal Information
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={user?.name || ''}
                    disabled
                    slotProps={{
                      input: {
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>
                            <IconUser size={18} />
                          </Box>
                        ),
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: isLight ? '#f8fafc !important' : 'rgba(30, 35, 50, 0.8) !important',
                        '& fieldset': {
                          borderColor: isLight ? 'rgba(0, 0, 0, 0.12) !important' : 'rgba(255, 255, 255, 0.08) !important',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'transparent !important',
                        background: 'transparent !important',
                        color: isLight ? '#475569 !important' : 'rgba(255, 255, 255, 0.7) !important',
                        WebkitTextFillColor: isLight ? '#475569 !important' : 'rgba(255, 255, 255, 0.7) !important',
                      },
                      '& .MuiInputLabel-root': {
                        color: isLight ? '#64748b !important' : 'rgba(255, 255, 255, 0.5) !important',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={user?.email || ''}
                    disabled
                    slotProps={{
                      input: {
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>
                            <IconMail size={18} />
                          </Box>
                        ),
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: isLight ? '#f8fafc !important' : 'rgba(30, 35, 50, 0.8) !important',
                        '& fieldset': {
                          borderColor: isLight ? 'rgba(0, 0, 0, 0.12) !important' : 'rgba(255, 255, 255, 0.08) !important',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'transparent !important',
                        background: 'transparent !important',
                        color: isLight ? '#475569 !important' : 'rgba(255, 255, 255, 0.7) !important',
                        WebkitTextFillColor: isLight ? '#475569 !important' : 'rgba(255, 255, 255, 0.7) !important',
                      },
                      '& .MuiInputLabel-root': {
                        color: isLight ? '#64748b !important' : 'rgba(255, 255, 255, 0.5) !important',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Security Section */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconLock size={18} style={{ color: isLight ? '#1e293b' : '#fafafa' }} />
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    fontFamily: '"Satoshi", sans-serif',
                    color: isLight ? '#1e293b' : '#fafafa',
                  }}
                >
                  Security
                </Typography>
              </Stack>
              {/* Info for OAuth users without password */}
              {!user?.hasPassword && (
                <Alert severity="info" sx={{ borderRadius: '10px', mb: 2 }}>
                  You logged in via OAuth. Set a password to also enable email/password login.
                </Alert>
              )}
              <Stack spacing={2}>
                {/* Only show current password field if user has a password */}
                {user?.hasPassword && (
                  <TextField
                    fullWidth
                    label="Current Password"
                    type="password"
                    placeholder="Enter current password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    disabled={isSubmitting}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <Box sx={{ mr: 1, display: 'flex', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>
                            <IconLock size={18} />
                          </Box>
                        ),
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isLight ? '#ffffff !important' : 'rgba(30, 35, 50, 0.6) !important',
                        borderRadius: '10px',
                        '& fieldset': {
                          borderColor: isLight ? 'rgba(0, 0, 0, 0.12) !important' : 'rgba(255, 255, 255, 0.1) !important',
                        },
                        '&:hover fieldset': {
                          borderColor: isLight ? 'rgba(0, 0, 0, 0.2) !important' : 'rgba(255, 255, 255, 0.15) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6366f1 !important',
                          borderWidth: '1px',
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        backgroundColor: 'transparent !important',
                        background: 'transparent !important',
                        color: isLight ? '#1e293b !important' : 'rgba(255, 255, 255, 0.9) !important',
                        '&::placeholder': {
                          color: isLight ? '#94a3b8 !important' : 'rgba(255, 255, 255, 0.4) !important',
                          opacity: 1,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: isLight ? '#64748b !important' : 'rgba(255, 255, 255, 0.5) !important',
                        '&.Mui-focused': {
                          color: '#6366f1 !important',
                        },
                      },
                    }}
                  />
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      placeholder={user?.hasPassword ? 'Leave blank to keep current' : 'Set your password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      disabled={isSubmitting}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Box sx={{ mr: 1, display: 'flex', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>
                              <IconKey size={18} />
                            </Box>
                          ),
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: isLight ? '#ffffff !important' : 'rgba(30, 35, 50, 0.6) !important',
                          borderRadius: '10px',
                          '& fieldset': {
                            borderColor: isLight ? 'rgba(0, 0, 0, 0.12) !important' : 'rgba(255, 255, 255, 0.1) !important',
                          },
                          '&:hover fieldset': {
                            borderColor: isLight ? 'rgba(0, 0, 0, 0.2) !important' : 'rgba(255, 255, 255, 0.15) !important',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6366f1 !important',
                            borderWidth: '1px',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          backgroundColor: 'transparent !important',
                          background: 'transparent !important',
                          color: isLight ? '#1e293b !important' : 'rgba(255, 255, 255, 0.9) !important',
                          '&::placeholder': {
                            color: isLight ? '#94a3b8 !important' : 'rgba(255, 255, 255, 0.4) !important',
                            opacity: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: isLight ? '#64748b !important' : 'rgba(255, 255, 255, 0.5) !important',
                          '&.Mui-focused': {
                            color: '#6366f1 !important',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                      placeholder="Confirm new password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      disabled={isSubmitting}
                      slotProps={{
                        input: {
                          startAdornment: (
                            <Box sx={{ mr: 1, display: 'flex', color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.5)' }}>
                              <IconKey size={18} />
                            </Box>
                          ),
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: isLight ? '#ffffff !important' : 'rgba(30, 35, 50, 0.6) !important',
                          borderRadius: '10px',
                          '& fieldset': {
                            borderColor: isLight ? 'rgba(0, 0, 0, 0.12) !important' : 'rgba(255, 255, 255, 0.1) !important',
                          },
                          '&:hover fieldset': {
                            borderColor: isLight ? 'rgba(0, 0, 0, 0.2) !important' : 'rgba(255, 255, 255, 0.15) !important',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#6366f1 !important',
                            borderWidth: '1px',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          backgroundColor: 'transparent !important',
                          background: 'transparent !important',
                          color: isLight ? '#1e293b !important' : 'rgba(255, 255, 255, 0.9) !important',
                          '&::placeholder': {
                            color: isLight ? '#94a3b8 !important' : 'rgba(255, 255, 255, 0.4) !important',
                            opacity: 1,
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: isLight ? '#64748b !important' : 'rgba(255, 255, 255, 0.5) !important',
                          '&.Mui-focused': {
                            color: '#6366f1 !important',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)'}` }}>
          <Button
            onClick={handleCloseSettings}
            disabled={isSubmitting}
            sx={{
              px: 3,
              py: 1,
              borderRadius: '10px',
              color: isLight ? '#475569' : 'rgba(255, 255, 255, 0.7)',
              background: isLight ? '#f8fafc' : 'rgba(255, 255, 255, 0.04)',
              border: `1px solid ${isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.9rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.08)',
                borderColor: isLight ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={isSubmitting}
            sx={{
              px: 3,
              py: 1,
              borderRadius: '10px',
              background: isLight ? '#0f172a' : '#ffffff',
              color: isLight ? '#ffffff' : '#0f172a',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              fontFamily: '"Satoshi", sans-serif',
              transition: 'all 0.2s ease',
              boxShadow: isLight
                ? '0 2px 8px rgba(15, 23, 42, 0.15)'
                : '0 2px 8px rgba(0, 0, 0, 0.2)',
              '&:hover': {
                background: isLight ? '#1e293b' : '#f1f5f9',
                transform: 'translateY(-1px)',
                boxShadow: isLight
                  ? '0 4px 12px rgba(15, 23, 42, 0.2)'
                  : '0 4px 12px rgba(0, 0, 0, 0.3)',
              },
              '&.Mui-disabled': {
                background: isLight ? 'rgba(15, 23, 42, 0.3)' : 'rgba(255, 255, 255, 0.3)',
                color: isLight ? 'rgba(255, 255, 255, 0.5)' : 'rgba(15, 23, 42, 0.5)',
                boxShadow: 'none',
              },
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
